import { zodResolver } from "@hookform/resolvers/zod";
import {
  Archive,
  ArchiveRestore,
  ArrowDown,
  ArrowUp,
  Layers,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { ActionCell } from "@/components/shared/action-cell";
import { ConfigFormDialog } from "@/components/shared/config-form-dialog";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { HideRetiredFilter } from "@/components/shared/hide-retired-filter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  type RoleLevelRow,
  useCreateRoleLevel,
  useDeleteRoleLevel,
  useReorderRoleLevels,
  useRoleLevels,
  useSetRoleLevelRetired,
  useUpdateRoleLevel,
} from "@/hooks/use-role-levels";
import { cn } from "@/lib/utils";
import { type CreateRoleLevelInput, createRoleLevelSchema } from "@/schemas/role-levels";

/**
 * Settings → Config → Role levels: the company-wide career ladder (WI-102).
 *
 * Distinct from Settings → Config → Levels, which is the fixed five-point
 * performance-rating scale shared by skills and competencies. These are career
 * levels a role opts into, the list is admin-managed, and it is deliberately
 * **not capped** — the final count is unsettled, so nothing here limits it.
 */
export function SettingsRoleLevelsPage() {
  const { data: levels = [], isLoading, isError, error, refetch } = useRoleLevels();
  const [editing, setEditing] = useState<RoleLevelRow | null>(null);
  const [adding, setAdding] = useState(false);
  const [hideRetired, setHideRetired] = useState(true);
  const reorder = useReorderRoleLevels();

  // The API sorts retired levels last, so the live rungs are the prefix and the
  // hidden ones are always a suffix. Ordering is a property of the live ladder
  // only — a retired level's position means nothing, which is why the arrows
  // below act on `live` and retired rows show no order number at all.
  const live = levels.filter((l) => !l.retired);
  const retiredCount = levels.length - live.length;
  const visible = hideRetired ? live : levels;

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load role levels"}
        onRetry={() => refetch()}
      />
    );
  }

  /**
   * Swap a live level with its neighbour and send the whole ladder back —
   * retired levels re-appended at the tail, since the endpoint renumbers from
   * the array it is given and they belong at the bottom either way.
   */
  function move(index: number, direction: -1 | 1) {
    const next = [...live];
    const target = next[index];
    const swap = next[index + direction];
    if (!target || !swap) return;
    next[index] = swap;
    next[index + direction] = target;
    reorder.mutate([...next, ...levels.filter((l) => l.retired)].map((l) => l.id));
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <p className="max-w-3xl text-sm text-muted-foreground">
          The career levels every role can opt into — which ones apply is chosen per role on the
          role's page. Each level's definition is company-wide: what Senior means for autonomy,
          decision-making, influence and mentoring is the same in every role. This is separate from{" "}
          <span className="font-medium text-foreground">Performance ratings</span>, the five-point
          scale skills and competencies are rated against.
        </p>
        <Button onClick={() => setAdding(true)} className="shrink-0">
          <Plus />
          Add level
        </Button>
      </div>

      <HideRetiredFilter
        id="role-levels-hide-retired"
        checked={hideRetired}
        onCheckedChange={setHideRetired}
        hiddenCount={retiredCount}
      />

      <DataTable
        headers={[
          { label: "Order" },
          { label: "Name" },
          { label: "Company-wide definition" },
          { label: "Used by" },
          { label: "Actions", className: "text-right" },
        ]}
        loading={isLoading}
        empty={
          <EmptyState
            icon={Layers}
            title="No role levels"
            description="The default ladder is seeded on server startup. Add a level to get started."
          />
        }
      >
        {visible.map((level, index) => (
          <tr
            key={level.id}
            className={cn(
              "border-b border-border last:border-0 transition-colors hover:bg-muted/30",
              level.retired && "opacity-60",
            )}
          >
            <td className="px-4 py-3">
              {level.retired ? (
                <span className="text-muted-foreground">—</span>
              ) : (
                <div className="flex items-center gap-1">
                  <span className="w-4 text-muted-foreground">{index + 1}</span>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Move ${level.name} up`}
                    disabled={index === 0 || reorder.isPending}
                    onClick={() => move(index, -1)}
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Move ${level.name} down`}
                    disabled={index === live.length - 1 || reorder.isPending}
                    onClick={() => move(index, 1)}
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </td>
            <td className="px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="font-medium">{level.name}</span>
                {level.retired && <Badge variant="secondary">Retired</Badge>}
              </div>
            </td>
            <td className="max-w-md px-4 py-3 text-sm text-muted-foreground">
              {level.definition ? (
                <span className="line-clamp-2">{level.definition}</span>
              ) : (
                <span className="italic text-muted-foreground/70">No definition yet.</span>
              )}
            </td>
            <td className="px-4 py-3 text-sm text-muted-foreground">{usageSummary(level)}</td>
            <td className="px-4 py-3 text-right">
              <RowActions level={level} onEdit={() => setEditing(level)} />
            </td>
          </tr>
        ))}
      </DataTable>

      <p className="max-w-3xl text-xs text-muted-foreground">
        Every change here is audited. <span className="font-medium">Retiring</span> is the normal
        way to take a level out of use: it stays readable, so past assignments, aspirations and
        written expectations still make sense. A level can only be{" "}
        <span className="font-medium">deleted</span> while nothing in the app refers to it at all —
        which is really for one added by mistake.
      </p>

      {adding && <RoleLevelDialog open onOpenChange={() => setAdding(false)} />}
      {editing && (
        <RoleLevelDialog
          key={editing.id}
          open
          onOpenChange={() => setEditing(null)}
          level={editing}
        />
      )}
    </div>
  );
}

/**
 * Everything referring to this level, in the reader's terms — the answer to
 * "can I delete this". Only non-zero parts appear, so the common case reads as
 * a short phrase rather than four zeroes.
 */
function usageSummary(level: RoleLevelRow): string {
  const { roles, people, aspirations, expectations, reviews } = level.usage;
  const parts: string[] = [];
  if (roles > 0) parts.push(`${roles} ${roles === 1 ? "role" : "roles"}`);
  if (people > 0) parts.push(`${people} ${people === 1 ? "person" : "people"}`);
  if (aspirations > 0) parts.push(`${aspirations} working toward it`);
  if (expectations > 0) {
    parts.push(`${expectations} ${expectations === 1 ? "expectation" : "expectations"}`);
  }
  if (reviews > 0) parts.push(`${reviews} review ${reviews === 1 ? "record" : "records"}`);
  return parts.length === 0 ? "Not used yet" : parts.join(" · ");
}

/**
 * Per-row menu.
 *
 * Both destructive actions are disabled up front rather than left to fail on
 * click, each with the reason in its own label — the reviewer's rule was to say
 * plainly what has to happen first. The two guards are deliberately DIFFERENT:
 *
 * - **Retire** is blocked only by roles opting into the level, because that is
 *   the one thing retirement would strip out from under a role.
 * - **Delete** is blocked by anything at all referring to the level (roles,
 *   people, aspirations, written expectations), because the database would
 *   cascade or null those without a trace. See `RoleLevelUsage`.
 */
function RowActions({ level, onEdit }: { level: RoleLevelRow; onEdit: () => void }) {
  const setRetired = useSetRoleLevelRetired(level.id);
  const remove = useDeleteRoleLevel(level.id);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const retireBlocked = !level.retired && level.roleCount > 0;
  const deleteBlocked = level.usageTotal > 0;

  return (
    <>
      <ActionCell
        items={[
          { icon: Pencil, label: "Edit", onClick: onEdit },
          level.retired
            ? {
                icon: ArchiveRestore,
                label: "Restore",
                onClick: () => setRetired.mutate(false),
                separatorBefore: true,
              }
            : {
                icon: Archive,
                label: retireBlocked
                  ? `In use by ${level.roleCount} ${level.roleCount === 1 ? "role" : "roles"}`
                  : "Retire",
                onClick: () => setRetired.mutate(true),
                variant: "destructive" as const,
                disabled: retireBlocked || setRetired.isPending,
                separatorBefore: true,
              },
          {
            icon: Trash2,
            label: deleteBlocked ? `Used by ${usageSummary(level)} — can't delete` : "Delete",
            onClick: () => setConfirmingDelete(true),
            variant: "destructive" as const,
            disabled: deleteBlocked || remove.isPending,
          },
        ]}
      />
      {confirmingDelete && (
        <ConfirmDialog
          open
          onOpenChange={() => setConfirmingDelete(false)}
          title={`Delete "${level.name}"?`}
          description="Nothing in the app refers to this level, so removing it loses nothing. This can't be undone — if you might want it back, retire it instead."
          confirmLabel="Delete"
          destructive
          onConfirm={() =>
            remove.mutate(undefined, { onSuccess: () => setConfirmingDelete(false) })
          }
        />
      )}
    </>
  );
}

/** Add / edit dialog. Same form both ways — only the copy and mutation differ. */
function RoleLevelDialog({
  open,
  onOpenChange,
  level,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  level?: RoleLevelRow;
}) {
  const create = useCreateRoleLevel();
  const update = useUpdateRoleLevel(level?.id ?? "");
  const isEdit = level !== undefined;

  const form = useForm<CreateRoleLevelInput>({
    resolver: zodResolver(createRoleLevelSchema),
    defaultValues: { name: level?.name ?? "", definition: level?.definition ?? null },
  });

  async function onSubmit(values: CreateRoleLevelInput) {
    if (isEdit) await update.mutateAsync(values);
    else await create.mutateAsync(values);
    onOpenChange(false);
  }

  const pending = create.isPending || update.isPending;

  return (
    <ConfigFormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? `Edit ${level.name}` : "Add role level"}
      description={
        <>
          The definition is company-wide — it describes what this level means in every role, not in
          one specific role. What a level expects of a particular skill is set on the role.
        </>
      }
      form={form}
      onSubmit={onSubmit}
      submitLabel={isEdit ? "Save" : "Add level"}
      pending={pending}
    >
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Name</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Senior" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="definition"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Company-wide definition</FormLabel>
            <FormControl>
              <Textarea
                rows={5}
                placeholder="Level of autonomy, decision-making, influence, how we expect someone to act on project teams, how they mentor others…"
                {...field}
                value={field.value ?? ""}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </ConfigFormDialog>
  );
}
